import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import { VERSION_INFO } from '../../config/version';

/**
 * PWAUpdatePrompt Component
 * Shows a prompt when a new service worker version is available
 */
export function PWAUpdatePrompt() {
  const { t } = useTranslation();
  const [showPrompt, setShowPrompt] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    // Register service worker and listen for updates
    if ('serviceWorker' in navigator) {
      const handleServiceWorkerUpdate = async () => {
        try {
          const registration = await navigator.serviceWorker.getRegistration();

          if (!registration) {
            // Register the service worker if not already registered
            const newRegistration = await navigator.serviceWorker.register('/sw.js');
            setupUpdateListener(newRegistration);
          } else {
            setupUpdateListener(registration);

            // Check for updates periodically (every 60 seconds)
            setInterval(() => {
              registration.update().catch(console.error);
            }, 60000);
          }
        } catch (error) {
          console.error('[PWA] Service worker registration failed:', error);
        }
      };

      handleServiceWorkerUpdate();

      // Listen for controller change (new SW took over)
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        // Only reload if we initiated the update
        if (isUpdating) {
          window.location.reload();
        }
      });
    }
  }, [isUpdating]);

  const setupUpdateListener = (registration: ServiceWorkerRegistration) => {
    // If there's already a waiting worker
    if (registration.waiting) {
      setWaitingWorker(registration.waiting);
      setShowPrompt(true);
      return;
    }

    // Listen for new installing workers
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (newWorker) {
        newWorker.addEventListener('statechange', () => {
          // New service worker is installed and waiting
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            setWaitingWorker(newWorker);
            setShowPrompt(true);
          }
        });
      }
    });
  };

  const handleUpdate = () => {
    if (!waitingWorker) return;

    setIsUpdating(true);

    // Tell the waiting worker to skip waiting and take control
    waitingWorker.postMessage({ type: 'SKIP_WAITING' });

    // Also clear old caches
    waitingWorker.postMessage({ type: 'CLEAR_CACHE' });
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Remember dismissal for this session only
    sessionStorage.setItem('pwa_update_dismissed', 'true');
  };

  // Don't show if dismissed this session
  if (sessionStorage.getItem('pwa_update_dismissed') === 'true') {
    return null;
  }

  if (!showPrompt) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 10000,
        maxWidth: '380px',
        width: 'calc(100% - 40px)',
        animation: 'slideInRight 0.3s ease-out'
      }}
    >
      <div
        className="card"
        style={{
          padding: '16px',
          backgroundColor: 'var(--card)',
          backdropFilter: 'blur(10px)',
          border: '2px solid #10b981',
          boxShadow: '0 8px 32px rgba(16, 185, 129, 0.3)',
          borderRadius: '12px'
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <div
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <Icons.RefreshCw size={22} style={{ color: 'white' }} />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              {t('pwa.updateAvailable', 'Update Available')}
              <span style={{
                fontSize: '10px',
                padding: '2px 6px',
                background: '#10b981',
                color: 'white',
                borderRadius: '4px',
                fontWeight: 600
              }}>
                v{VERSION_INFO.version}
              </span>
            </div>
            <div style={{ fontSize: '13px', color: 'var(--muted)', lineHeight: '1.4' }}>
              {t('pwa.updateDescription', 'A new version is available with improvements and bug fixes.')}
            </div>
          </div>

          <button
            onClick={handleDismiss}
            style={{
              background: 'transparent',
              border: 'none',
              padding: '4px',
              cursor: 'pointer',
              color: 'var(--muted)',
              borderRadius: '6px'
            }}
            title={t('common.close', 'Close')}
          >
            <Icons.X size={18} />
          </button>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '8px', marginTop: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={handleDismiss}
            className="btn-ghost"
            style={{ fontSize: '13px', padding: '8px 16px' }}
          >
            {t('pwa.later', 'Later')}
          </button>
          <button
            onClick={handleUpdate}
            disabled={isUpdating}
            style={{
              fontSize: '13px',
              padding: '8px 16px',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: isUpdating ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontWeight: 600,
              opacity: isUpdating ? 0.7 : 1
            }}
          >
            {isUpdating ? (
              <>
                <Icons.RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} />
                {t('pwa.updating', 'Updating...')}
              </>
            ) : (
              <>
                <Icons.RefreshCw size={14} />
                {t('pwa.updateNow', 'Update Now')}
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
